// src/pages/CaptureGuidePage.tsx
import * as React from "react";
import {
  motion,
  useReducedMotion,
  useInView,
  type Variants,
  type Transition,
  type UseInViewOptions,
} from "framer-motion";
import { navigateTo } from "../utils/navigation";

import A1Hero from "../components/illustrations/A1Hero";
import A9DoDontRecap from "../components/illustrations/A9DoDontRecap";
import A10ChecklistStart from "../components/illustrations/A10ChecklistStart";
import A12Troubleshooting from "../components/illustrations/A12Troubleshooting";

/* Render-prop wrapper: notifies children when entering the viewport
   and increments a key so animations restart on re-entry. */
function InViewReplay({
  margin = "-15% 0px -15% 0px",
  amount = 0.35,
  children,
}: {
  margin?: UseInViewOptions['margin'];
  amount?: number;
  children: (opts: { inView: boolean; replayKey: number }) => React.ReactNode;
}) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const inView = useInView(ref, { margin, amount });
  const [k, setK] = React.useState(0);
  React.useEffect(() => {
    if (inView) setK((x) => x + 1);
  }, [inView]);
  return <div ref={ref}>{children({ inView, replayKey: k })}</div>;
}

export default function CaptureGuidePage(): React.JSX.Element {
  const prefersReduced = useReducedMotion();
  const spring: Transition = prefersReduced
    ? { duration: 0 }
    : { type: "spring", stiffness: 420, damping: 36, mass: 0.9 };

  const pageIn: Variants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: spring },
  };

  const listIn: Variants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: prefersReduced ? 0 : 0.06,
        delayChildren: prefersReduced ? 0 : 0.04,
      },
    },
  };

  const sectionIn: Variants = {
    hidden: { opacity: 0, y: 8 },
    visible: { opacity: 1, y: 0, transition: spring },
  };

  return (
    <motion.div
      variants={listIn}
      initial="hidden"
      animate="visible"
      className="stack-6"
      style={{ overflowX: "clip" }}
    >
      {/* Hero / header */}
      <motion.header variants={pageIn} className="card card--bordered">
        <div className="g-2" style={{ gap: "var(--s-6)" }}>
          {/* Text column */}
          <div className="stack-4">
            <div className="stack-2">
              <span className="t-label">Photo Guide</span>
              <h1 className="t-h2">
                Photo &amp; Scan <em className="t-italic">Guide</em>
              </h1>
            </div>
            <p className="t-body t-mute">
              Follow these tips for clean, consistent photos. Better inputs
              mean sharper analysis and more relevant recommendations.
            </p>
            <div className="row-3" style={{ flexWrap: "wrap" }}>
              <button
                onClick={() => { navigateTo("/new"); }}
                className="btn btn--primary"
              >
                Start a new analysis
              </button>
              <button
                onClick={() => { navigateTo("/history"); }}
                className="btn btn--ghost"
              >
                View my results
              </button>
            </div>
            <p className="t-small t-mute">
              You can revisit this guide any time from the help menu.
            </p>
          </div>

          {/* Illustration column — min-height reserves space to prevent layout jump on load */}
          <div
            style={{
              width: "100%",
              minHeight: "260px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <InViewReplay>
              {({ inView, replayKey }) => (
                <A1Hero
                  className="w-full h-auto t-teal"
                  play={inView}
                  replayKey={replayKey}
                />
              )}
            </InViewReplay>
          </div>
        </div>
      </motion.header>

      {/* A9: Do/Don't recap — illustration only */}
      <motion.section
        variants={sectionIn}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false, amount: 0.25 }}
      >
        <InViewReplay>
          {({ replayKey }) => (
            <A9DoDontRecap key={replayKey} className="w-full h-auto" />
          )}
        </InViewReplay>
      </motion.section>

      {/* A10: Ready to capture — illustration only */}
      <motion.section
        variants={sectionIn}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false, amount: 0.3 }}
      >
        <InViewReplay>
          {({ replayKey }) => (
            <A10ChecklistStart key={replayKey} className="w-full h-auto" />
          )}
        </InViewReplay>
      </motion.section>

      {/* A12: Troubleshooting — illustration only */}
      <motion.section
        variants={sectionIn}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false, amount: 0.3 }}
      >
        <InViewReplay>
          {({ replayKey }) => (
            <A12Troubleshooting key={replayKey} className="w-full h-auto" />
          )}
        </InViewReplay>
      </motion.section>

      {/* Footer CTA */}
      <motion.section variants={pageIn} className="card card--bordered">
        <div className="stack-4">
          <div className="stack-2">
            <h3 className="t-h3">You&apos;re set</h3>
            <p className="t-body t-mute">
              Great inputs make great outputs. When you&apos;re ready, start a new
              analysis or return to your results.
            </p>
          </div>
          <div className="row-3" style={{ flexWrap: "wrap" }}>
            <button
              onClick={() => { navigateTo("/new"); }}
              className="btn btn--primary"
            >
              Start a new analysis
            </button>
            <button
              onClick={() => { navigateTo("/history"); }}
              className="btn btn--ghost"
            >
              View my results
            </button>
          </div>
          <p className="t-small t-mute">
            For cosmetic & educational use only. Not a medical service.
          </p>
        </div>
      </motion.section>
    </motion.div>
  );
}
